import { LegislationMetadata } from "../models/legislation/Legislation";
import { ArvidMetadata } from "@/models/legislation/metadata/Arvid";
import { DomsdatabasenMetadata } from "@/models/legislation/metadata/Domsdatabasen";
import { EurLexMetadata } from "@/models/legislation/metadata/EURLex";
import { RetsinformationMetadata } from "@/models/legislation/metadata/Retsinformation";

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if metadata is from Arvid
 */
export function isArvidMetadata(
  metadata: LegislationMetadata | null | undefined
): metadata is ArvidMetadata {
  if (!metadata) return false;
  return (
    "journalNumber" in metadata &&
    "classification" in metadata &&
    "taxonomy" in metadata
  );
}

/**
 * Type guard to check if metadata is from Domsdatabasen
 */
export function isDomsdatabasenMetadata(
  metadata: LegislationMetadata | null | undefined
): metadata is DomsdatabasenMetadata {
  if (!metadata) return false;
  return (
    "caseSubjects" in metadata &&
    "courtCaseNumber" in metadata &&
    "verdictStatus" in metadata
  );
}

/**
 * Type guard to check if metadata is from EURLex
 */
export function isEurLexMetadata(
  metadata: LegislationMetadata | null | undefined
): metadata is EurLexMetadata {
  if (!metadata) return false;
  return (
    "celexNumber" in metadata && "eli" in metadata && "documentType" in metadata
  );
}

/**
 * Type guard to check if metadata is from Retsinformation
 */
export function isRetsinformationMetadata(
  metadata: LegislationMetadata | null | undefined
): metadata is RetsinformationMetadata {
  if (!metadata) return false;
  return (
    "accessionNumber" in metadata &&
    "documentTypeId" in metadata &&
    "retsinfoKlassifikationId" in metadata
  );
}

// ============================================================================
// HELPER FUNCTIONS - Extract common fields from different metadata types
// ============================================================================

/**
 * Get display title from metadata with fallback
 */
export function getMetadataTitle(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  // Check for title field (most sources)
  if ("title" in metadata && metadata.title) {
    return metadata.title;
  }

  // Check for headline (Domsdatabasen)
  if (isDomsdatabasenMetadata(metadata) && metadata.headline) {
    return metadata.headline;
  }

  return null;
}

/**
 * Get publication date from metadata with fallback
 * Returns ISO date string or null
 */
export function getMetadataPublicationDate(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  // Arvid
  if (isArvidMetadata(metadata)) {
    return metadata.publishedOn || metadata.rulingOn || null;
  }

  // Retsinformation
  if (isRetsinformationMetadata(metadata)) {
    return metadata.publishedDate || metadata.signatureDate || null;
  }

  // EURLex
  if (isEurLexMetadata(metadata)) {
    return metadata.processedAt || null;
  }

  return null;
}

/**
 * Get authority/source authority from metadata
 */
export function getMetadataAuthority(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isArvidMetadata(metadata)) {
    return metadata.authority?.name || metadata.authority?.long_name || null;
  }

  if (isRetsinformationMetadata(metadata)) {
    return metadata.adminAuthority || null;
  }

  return null;
}

/**
 * Get abstract/summary/description from metadata
 */
export function getMetadataAbstract(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isArvidMetadata(metadata)) {
    return metadata.summary || null;
  }

  return null;
}

/**
 * Get PDF URL from metadata if available
 */
export function getMetadataPdfUrl(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isRetsinformationMetadata(metadata)) {
    return metadata.pdfLink?.isPdfAvailable
      ? metadata.pdfLink.url || null
      : null;
  }

  if (isEurLexMetadata(metadata)) {
    return metadata.pdfUrl || null;
  }

  if (
    isDomsdatabasenMetadata(metadata) &&
    metadata.pdfUrls &&
    metadata.pdfUrls.length > 0
  ) {
    return metadata.pdfUrls[0];
  }

  return null;
}

/**
 * Get HTML URL from metadata if available
 */
export function getMetadataHtmlUrl(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isEurLexMetadata(metadata)) {
    return metadata.htmlUrl || null;
  }

  return null;
}

/**
 * Get keywords/tags from metadata
 */
export function getMetadataKeywords(
  metadata: LegislationMetadata | null | undefined
): string[] {
  if (!metadata) return [];

  if (isRetsinformationMetadata(metadata) && metadata.keywords) {
    return metadata.keywords;
  }

  if (isArvidMetadata(metadata) && metadata.labels) {
    return metadata.labels;
  }

  if (isDomsdatabasenMetadata(metadata)) {
    const tags: string[] = [];
    if (metadata.caseTags) tags.push(...metadata.caseTags);
    if (metadata.caseSubjects) tags.push(...metadata.caseSubjects);
    return tags;
  }

  return [];
}

/**
 * Check if document has special status flags
 */
export function getMetadataStatusFlags(
  metadata: LegislationMetadata | null | undefined
): {
  isHistorical?: boolean;
  isNullified?: boolean;
  isRevoked?: boolean;
} {
  if (!metadata) return {};

  const flags: ReturnType<typeof getMetadataStatusFlags> = {};

  if (isRetsinformationMetadata(metadata)) {
    flags.isHistorical = metadata.isHistorical;
  }

  if (isArvidMetadata(metadata)) {
    flags.isNullified = metadata.nullified;
  }

  if (isDomsdatabasenMetadata(metadata)) {
    flags.isRevoked = !!metadata.revokedDateTime;
  }

  return flags;
}

/**
 * Get case/journal number from metadata
 */
export function getMetadataCaseNumber(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isArvidMetadata(metadata)) {
    return metadata.journalNumber || null;
  }

  if (isDomsdatabasenMetadata(metadata)) {
    return metadata.courtCaseNumber || metadata.caseworkerCaseNumber || null;
  }

  return null;
}

/**
 * Get accession/document identifier
 */
export function getMetadataIdentifier(
  metadata: LegislationMetadata | null | undefined
): string | null {
  if (!metadata) return null;

  if (isRetsinformationMetadata(metadata)) {
    return metadata.accessionNumber || metadata.uniqueDocumentId || null;
  }

  if (isEurLexMetadata(metadata)) {
    return metadata.celexNumber || metadata.eli || null;
  }

  if (
    isDomsdatabasenMetadata(metadata) &&
    metadata.documents &&
    metadata.documents.length > 0
  ) {
    return metadata.documents[0].ecli || metadata.documents[0].id || null;
  }

  return null;
}

// ============================================================================
// UI UTILITY FUNCTIONS (for existing components)
// ============================================================================

/**
 * Format date value for display
 */
export function dateValue(date: string | null | undefined): string {
  if (!date) return "";

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return date; // Return original if invalid

    return d.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return date;
  }
}

/**
 * Parse EurLex title to extract number and date
 * Pattern: number first, then date (sometimes number has year prefix)
 * Examples:
 * - "32019R1234 af 12.03.2019"
 * - "2019/1234 12-03-2019"
 * - "R1234/2019 12.03.2019"
 * - "nr. 650/2012 af 4. juli 2012" (Danish month names)
 * - "Kommissionens afgørelse (EU) 2015/2432 af 18. september 2015"
 */
export function parseEurLexTitle(title: string | null | undefined): {
  number?: string;
  date?: string;
} {
  if (!title) return {};

  const result: { number?: string; date?: string } = {};

  // Danish month names mapping
  const danishMonths: Record<string, number> = {
    januar: 1,
    februar: 2,
    marts: 3,
    april: 4,
    maj: 5,
    juni: 6,
    juli: 7,
    august: 8,
    september: 9,
    oktober: 10,
    november: 11,
    december: 12,
  };

  // Pattern for Danish date format: "af DD. [month name] YYYY"
  // Example: "af 4. juli 2012", "af 28. oktober 2009"
  const danishDatePattern = /\baf\s+(\d{1,2})\.\s+([a-z]+)\s+(\d{4})\b/i;

  // Common numeric date patterns: DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD
  const numericDatePatterns = [
    /\b(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{4})\b/, // DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY
    /\b(\d{4}[.\-/]\d{1,2}[.\-/]\d{1,2})\b/, // YYYY-MM-DD, YYYY.MM.DD, YYYY/MM/DD
    /\b(\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2})\b/, // DD.MM.YY, DD/MM/YY, DD-MM-YY
  ];

  // Extract date FIRST to know where the primary document number should be (before the date)
  let dateMatch: RegExpMatchArray | null = null;
  let dateStr: string | null = null;
  let dateIndex = -1;

  // First, try to find Danish date format (most common in EurLex)
  const danishMatch = title.match(danishDatePattern);
  if (danishMatch) {
    const day = parseInt(danishMatch[1], 10);
    const monthName = danishMatch[2].toLowerCase();
    const year = parseInt(danishMatch[3], 10);
    const month = danishMonths[monthName];

    if (month) {
      try {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          dateStr = date.toISOString();
          dateMatch = danishMatch;
          dateIndex = danishMatch.index ?? -1;
        }
      } catch {
        // Fall through to numeric patterns
      }
    }
  }

  // If Danish date not found, try numeric date patterns
  if (!dateMatch) {
    for (const pattern of numericDatePatterns) {
      const match = title.match(pattern);
      if (match) {
        dateMatch = match;
        dateStr = match[1];
        dateIndex = match.index ?? -1;
        break;
      }
    }
  }

  // Extract date if found
  if (dateMatch && dateStr) {
    // If it's a numeric date string, try to parse it
    if (!dateStr.includes("T") && !dateStr.includes("Z")) {
      // It's not already an ISO string, parse it
      try {
        // Replace dots/slashes with dashes for consistent parsing
        const normalizedDate = dateStr.replace(/[.\/]/g, "-");
        const date = new Date(normalizedDate);
        if (!isNaN(date.getTime())) {
          result.date = date.toISOString();
        } else {
          result.date = dateStr;
        }
      } catch {
        result.date = dateStr;
      }
    } else {
      // Already an ISO string from Danish date parsing
      result.date = dateStr;
    }
  }

  // Extract number - prioritize patterns that appear BEFORE the date (primary document number)
  // But also check after date for patterns like "K(YYYY) NNNN" that appear at the end
  const beforeDate = dateIndex >= 0 ? title.substring(0, dateIndex) : title;
  const afterDate =
    dateIndex >= 0 && dateMatch
      ? title.substring(dateIndex + (dateMatch[0]?.length ?? 0))
      : "";

  // Number patterns - prioritize primary document identifiers
  // Each pattern is an object with the regex and a function to extract the number
  const numberPatterns: Array<{
    pattern: RegExp;
    extract: (match: RegExpMatchArray) => string;
    priority: number; // Lower number = higher priority
    searchAfterDate?: boolean; // Some patterns like "K(YYYY) NNNN" appear after the date
  }> = [
    // Pattern: "(EU) YYYY/NNNN" or "(EF) YYYY/NNNN" - most common format for primary documents
    {
      pattern: /\(EU\)\s+(\d{4}\/\d+)/i,
      extract: (m) => m[1],
      priority: 1,
    },
    {
      pattern: /\(EF\)\s+(\d{4}\/\d+)/i,
      extract: (m) => m[1],
      priority: 1,
    },
    // Pattern: "K(YYYY) NNNN" - Commission decision format (e.g., "K(2009) 8064")
    // This often appears at the end of the title, so search after date too
    {
      pattern: /K\((\d{4})\)\s+(\d+)/i,
      extract: (m) => `K(${m[1]}) ${m[2]}`,
      priority: 1,
      searchAfterDate: true,
    },
    // Pattern: "YYYY/NNNN/EF" at start of title
    {
      pattern: /^(\d{4}\/\d+\/EF)/i,
      extract: (m) => m[1],
      priority: 2,
    },
    // Pattern: "YYYY/NNNN" at start of title
    {
      pattern: /^(\d{4}\/\d+)/,
      extract: (m) => m[1],
      priority: 2,
    },
    // Pattern: "nr. NNNN/YYYY" - but only if it appears early (likely primary document)
    // Check if it's within first 200 characters to avoid referenced laws
    {
      pattern: /\bnr\.\s+(\d+\/\d+)/i,
      extract: (m) => m[1],
      priority: 3,
    },
    // Pattern: "YYYY/NNNN" anywhere before date (but not part of a date)
    {
      pattern: /\b(\d{4}\/\d{3,})\b/,
      extract: (m) => m[1],
      priority: 4,
    },
    // Pattern: Generic number with slashes before date (e.g., "R1234/2019")
    {
      pattern: /\b([A-Z]?\d+[A-Z]?\d*\/\d+[A-Z]?\d*)\b/,
      extract: (m) => m[1],
      priority: 5,
    },
  ];

  let extractedNumber: string | null = null;
  let bestMatch: { number: string; index: number; priority: number } | null =
    null;

  // Search in the text before the date first (primary document number should be there)
  for (const { pattern, extract, priority } of numberPatterns) {
    const match = beforeDate.match(pattern);
    if (match) {
      const matchIndex = match.index ?? -1;
      const number = extract(match);

      // Prefer matches with higher priority (lower number) and earlier position
      if (
        !bestMatch ||
        priority < bestMatch.priority ||
        (priority === bestMatch.priority && matchIndex < bestMatch.index)
      ) {
        // For "nr. NNNN/YYYY" pattern, only accept if it's early in the title
        // (to avoid matching referenced laws like "regionallov nr. 32")
        if (pattern.source.includes("nr\\.\\s+")) {
          if (matchIndex < 200) {
            // Only accept if within first 200 characters
            bestMatch = { number, index: matchIndex, priority };
          }
        } else {
          bestMatch = { number, index: matchIndex, priority };
        }
      }
    }
  }

  // Also search after date for patterns that typically appear there (like "K(YYYY) NNNN")
  if (!bestMatch || bestMatch.priority > 2) {
    for (const {
      pattern,
      extract,
      priority,
      searchAfterDate,
    } of numberPatterns) {
      if (searchAfterDate && afterDate) {
        const match = afterDate.match(pattern);
        if (match) {
          const matchIndex =
            (dateIndex >= 0 ? dateIndex : 0) + (match.index ?? 0);
          const number = extract(match);

          // Only use if we don't have a match or this has higher priority
          if (
            !bestMatch ||
            priority < bestMatch.priority ||
            (priority === bestMatch.priority && matchIndex < bestMatch.index)
          ) {
            bestMatch = { number, index: matchIndex, priority };
          }
        }
      }
    }
  }

  if (bestMatch) {
    extractedNumber = bestMatch.number;
  }

  // If no number found, try patterns in the full title (for cases without dates)
  if (!extractedNumber) {
    for (const { pattern, extract } of numberPatterns) {
      const match = title.match(pattern);
      if (match) {
        extractedNumber = extract(match);
        break;
      }
    }
  }

  if (extractedNumber) {
    result.number = extractedNumber;
  }

  return result;
}

/**
 * CSS classes for metadata display
 */
export const metadataStyle = {
  dialogContentStyle: "p-0",
  metadataTitleStyle: "text-muted-foreground",
  metadataValueStyle: "text-foreground  whitespace-normal break-words",
};

/**
 * Label map for Arvid metadata fields
 */
export const arvidLabelMap: Record<string, string> = {
  archived: "Arkiveret",
  authority: "Myndighed",
  classification: "Klassifikation",
  createdAt: "Oprettet",
  journalNumber: "Journalnummer",
  labels: "Emner",
  nullified: "Ophævet",
  nullifiedOn: "Ophævet dato",
  published: "Udgivet",
  publishedOn: "Udgivelsesdato",
  rulingOn: "Afgørelsesdato",
  sources: "Retskilder",
  summary: "Resumé",
  taxonomy: "Taksonomi",
  title: "Titel",
  type: "Dokumenttype",
};

/**
 * Label map for Domsdatabasen metadata fields
 */
export const domsdatabasenLabelMap: Record<string, string> = {
  caseSubjects: "Sagsemner",
  caseTags: "Sagstags",
  caseType: "Sagstype",
  caseworkerCaseNumber: "Domsdatabasens sagsnummer",
  closedCourtroom: "Lukket retssag",
  courtCaseNumber: "Rettens sagsnummer",
  documents: "Dokumenter",
  headline: "Overskrift",
  instance: "Instans",
  monetaryClaim: "Påstandsbeløb",
  officeName: "Ret",
  participants: "Sagsparter",
  pdfUrls: "PDF URLs",
  profession: "Faggruppe",
  revokedDateTime: "Tilbagekaldt dato",
  verdictStatus: "Afgørelsesstatus",
};

/**
 * Label map for Domsdatabasen document fields
 */
export const domsdatabasenDocumentLabelMap: Record<string, string> = {
  id: "ID",
  ecli: "ECLI",
  references: "Henvisninger",
  displayTitle: "Dokumenttitel",
  documentType: "Dokumenttype",
  revokedDateTime: "Tilbagekaldt dato",
  verdictDateTime: "Domsdato",
};

export const eurLexLabelMap: Record<string, string> = {
  celexNumber: "CELEX-nummer",
  documentType: "Dokumenttype",
  eli: "ELI",
  title: "Titel",
  processedAt: "Behandlet",
  pdfUrl: "PDF URL",
  htmlUrl: "HTML URL",
};
